import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Music, Check, Loader2, Calendar, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TAP_IN_LOGO = 'https://customer-assets.emergentagent.com/job_playlisthub-9/artifacts/71bf539q_Tap%20In%20logo.jpg';
const TAP_IN_THUMBPRINT = 'https://customer-assets.emergentagent.com/job_1986475c-b426-43c8-877c-14f811ca84d9/artifacts/faivw91c_image%20%281%29%201.svg';

const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const EventGuestTapIn = ({ event, eventId, onClose }) => {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { isDarkMode } = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showNextAction, setShowNextAction] = useState(false);
  const [lastSubmittedSongs, setLastSubmittedSongs] = useState([]);
  const [songsAdded, setSongsAdded] = useState(0);
  const [allSlotsUsed, setAllSlotsUsed] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const countdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const songsPerGuest = event?.caps?.songs_per_guest || 5;
  const songsRemaining = Math.max(0, songsPerGuest - songsAdded - selectedSongs.length);

  // Fetch how many songs this guest has already added — GUARDRAIL
  useEffect(() => {
    const fetchGuestStatus = async () => {
      try {
        const guestsRes = await api.get(`/api/events/${eventId}/guests`);
        const myGuest = (guestsRes.data || []).find(g => g.user_id === user?.id);
        if (myGuest) {
          const added = myGuest.songs_added || 0;
          setSongsAdded(added);
          const cap = event?.caps?.songs_per_guest || 5;
          if (added >= cap) {
            setAllSlotsUsed(true);
            setSubmitted(true);
            startCountdown();
          }
        }
      } catch (err) {
        console.error('Failed to fetch guest status:', err);
      }
    };
    fetchGuestStatus();
  }, [eventId, user?.id, api, event?.caps?.songs_per_guest]);

  // Search songs via Apple Music
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `${API_URL}/api/music/search?term=${encodeURIComponent(searchQuery)}&types=songs&limit=6`
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const startCountdown = () => {
    setCountdown(25);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          if (onClose) {
            onClose();
          } else {
            navigate('/app', { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Toggle song selection (cart style)
  const toggleSongSelection = (song) => {
    const songId = song.id;
    const isSelected = selectedSongs.some(s => s.id === songId);

    if (isSelected) {
      setSelectedSongs(prev => prev.filter(s => s.id !== songId));
    } else {
      if (songsRemaining <= 0) return; // Can't select more than allowed
      setSelectedSongs(prev => [...prev, {
        id: song.id,
        title: song.name || song.title,
        artist: song.artist_name || song.artist || song.artistName || '',
        artwork_url: song.artwork_url || song.artworkUrl,
        genre: song.genre || ''
      }]);
    }
  };

  // Submit all selected songs
  const handleSubmit = async () => {
    if (selectedSongs.length === 0 || submitting) return;
    setSubmitting(true);
    setError('');

    try {
      for (const song of selectedSongs) {
        await api.post(`/api/events/${eventId}/songs/add`, {
          id: song.id,
          title: song.title,
          artist: song.artist,
          artwork_url: song.artwork_url,
          genre: song.genre
        });
      }

      const newCount = songsAdded + selectedSongs.length;
      setSongsAdded(newCount);

      if (newCount >= songsPerGuest) {
        // No slots left — go straight to final
        setAllSlotsUsed(true);
        setLastSubmittedSongs(prev => [...prev, ...selectedSongs]);
        setSelectedSongs([]);
        setSubmitted(true);
        startCountdown();
      } else {
        // Slots remain — show "Tap In Another Song" / "Submit Songs" choice
        setLastSubmittedSongs(prev => [...prev, ...selectedSongs]);
        setShowNextAction(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit songs';
      setError(detail);
      if (detail.includes('limit') || detail.includes('full')) {
        setAllSlotsUsed(true);
        setSubmitted(true);
        startCountdown();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (onClose) {
      onClose();
    } else {
      navigate('/app', { replace: true });
    }
  };

  const handleTapInAnother = () => {
    setShowNextAction(false);
    setSelectedSongs([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleFinalSubmit = () => {
    setShowNextAction(false);
    setSelectedSongs([]);
    setSubmitted(true);
    startCountdown();
  };

  const isSongSelected = (songId) => selectedSongs.some(s => s.id === songId);

  // Popup content
  const renderContent = () => {
    // Final confirmation — all done
    if (submitted) {
      return (
        <div className="text-center py-6 px-4">
          <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mb-4 mx-auto">
            <Check className="w-7 h-7 text-green-400" />
          </div>
          <h2 className={`text-lg font-bold mb-1.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Your songs have been added
          </h2>
          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {songsAdded} of {songsPerGuest} song{songsPerGuest !== 1 ? 's' : ''} used
          </p>

          {/* List of songs added */}
          {lastSubmittedSongs.length > 0 && (
            <div className="mt-3 space-y-1.5 text-left">
              {lastSubmittedSongs.map((s, i) => (
                <div key={i} className={`flex items-center gap-2.5 p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <div className="w-8 h-8 rounded-md overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 flex-shrink-0">
                    {s.artwork_url ? (
                      <img src={s.artwork_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{s.title}</p>
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.artist}</p>
                  </div>
                  <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {countdown !== null && (
            <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Closing in {countdown}s...
            </p>
          )}
          <button
            onClick={handleExit}
            className={`w-full mt-4 py-3 rounded-2xl font-semibold text-sm bg-gradient-to-r from-purple-600 to-blue-500 text-white`}
            data-testid="exit-tapin-btn"
          >
            Done
          </button>
        </div>
      );
    }

    // Choose next action — after tapping in, before final
    if (showNextAction) {
      const slotsLeft = Math.max(0, songsPerGuest - songsAdded);
      return (
        <div className="px-4 py-6">
          <p className={`text-center text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Songs added! {slotsLeft} song{slotsLeft !== 1 ? 's' : ''} remaining
          </p>

          <div className="space-y-3">
            <button
              onClick={handleTapInAnother}
              className="w-full py-4 rounded-2xl font-semibold text-base bg-gradient-to-r from-purple-600 to-blue-500 text-white active:scale-[0.98] transition-transform"
              data-testid="tapin-another-btn"
            >
              Tap In Another Song
            </button>
            <button
              onClick={handleFinalSubmit}
              className={`w-full py-4 rounded-2xl font-semibold text-base ${isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}
              data-testid="submit-songs-final-btn"
            >
              Submit Songs
            </button>
          </div>
        </div>
      );
    }

    // Main search + select view
    return (
      <div className="px-4 pb-4">
        {/* Header with logo */}
        <div className="text-center mb-5 pt-2">
          <img src={TAP_IN_LOGO} alt="Tap In" className="w-10 h-10 mx-auto mb-3 rounded-full" />
          <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Tap In a Song
          </h2>
          <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-purple-400/80' : 'text-purple-500/80'}`}>
            {songsRemaining} song{songsRemaining !== 1 ? 's' : ''} remaining
          </p>
        </div>

        {/* Search bar */}
        <div className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl mb-3 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
          <Search className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for a song..."
            className={`flex-1 bg-transparent outline-none text-sm ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
            autoFocus
            data-testid="tapin-search-input"
          />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
              <X className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        {/* Loading */}
        {searching && (
          <div className="flex justify-center py-6">
            <Loader2 className={`w-5 h-5 animate-spin ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
        )}

        {/* Search results - scrollable */}
        {!searching && searchResults.length > 0 && (
          <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
            {searchResults.map((song) => {
              const selected = isSongSelected(song.id);
              return (
                <div
                  key={song.id}
                  onClick={() => toggleSongSelection(song)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-xl transition-all cursor-pointer ${
                    selected
                      ? 'bg-green-500/15 border border-green-500/30'
                      : isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  data-testid={`song-result-${song.id}`}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 flex-shrink-0">
                    {(song.artwork_url || song.artworkUrl) ? (
                      <img src={song.artwork_url || song.artworkUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${selected ? 'text-green-600' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {song.name || song.title}
                    </p>
                    <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {song.artist_name || song.artist || song.artistName}
                    </p>
                  </div>
                  {selected ? (
                    <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={TAP_IN_THUMBPRINT} alt="Tap In" className="w-7 h-7" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
          <div className="text-center py-6">
            <Music className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No songs found for "{searchQuery}"
            </p>
          </div>
        )}

        {/* Prompt when no search */}
        {!searching && searchQuery.length < 2 && searchResults.length === 0 && (
          <div className="text-center py-8">
            <Music className={`w-10 h-10 mx-auto mb-2 ${isDarkMode ? 'text-gray-700' : 'text-gray-200'}`} />
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Search for a song to add to the playlist
            </p>
          </div>
        )}

        {/* Submit button — always visible when songs selected */}
        {selectedSongs.length > 0 && (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full mt-4 py-3.5 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-500 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform"
            data-testid="submit-songs-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Tap In!'
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-5" data-testid="tapin-popup">
      {/* Transparent backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/10 backdrop-blur-md"
        onClick={handleExit}
      />

      {/* Centered popup card */}
      <div
        className={`relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-pop-in ${
          isDarkMode ? 'bg-[#1a1a1a] border border-white/5' : 'bg-white shadow-xl'
        }`}
        style={{ maxHeight: '85vh' }}
      >
        {/* Cover image header (if host added one) */}
        {event?.cover_image && (
          <div className="relative h-32 overflow-hidden">
            <img src={event.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            {/* X button over cover */}
            <button
              onClick={handleExit}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center"
              data-testid="close-tapin-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            {/* Event name over cover */}
            <div className="absolute bottom-3 left-0 right-0 text-center px-4">
              <h3 className="text-base font-bold text-white">{event?.title}</h3>
              {(event?.date || event?.location) && (
                <div className="flex items-center justify-center gap-2 mt-0.5 flex-wrap">
                  {event?.date && (
                    <span className="flex items-center gap-1 text-xs text-white/70">
                      <Calendar className="w-3 h-3" />
                      {formatEventDate(event.date)}
                    </span>
                  )}
                  {event?.date && event?.location && (
                    <span className="text-xs text-white/40">·</span>
                  )}
                  {event?.location && (
                    <span className="flex items-center gap-1 text-xs text-white/70">
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Text header (no cover image) */}
        {!event?.cover_image && (
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 text-center pl-8">
                <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {event?.title}
                </h3>
                {(event?.date || event?.location) && (
                  <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                    {event?.date && (
                      <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <Calendar className="w-3 h-3" />
                        {formatEventDate(event.date)}
                      </span>
                    )}
                    {event?.date && event?.location && (
                      <span className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>·</span>
                    )}
                    {event?.location && (
                      <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={handleExit}
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}
                data-testid="close-tapin-btn"
              >
                <X className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        )}

        {/* Scrollable content */}
        <div className="overflow-y-auto" style={{ maxHeight: event?.cover_image ? 'calc(85vh - 128px)' : 'calc(85vh - 60px)' }}>
          {renderContent()}
        </div>
      </div>

      <style>{`
        @keyframes pop-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-pop-in {
          animation: pop-in 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EventGuestTapIn;
